import { Directive, ElementRef, HostListener } from '@angular/core';

/**
 * Restricts a name field to letters, spaces, apostrophes and hyphens.
 *
 * Typed symbols are dropped at the keypress so they never appear on screen.
 * Pasted, dragged and autofilled text is stripped on input instead, since
 * none of those fire a keypress.
 */

/** Everything a name may not contain. Latin letters, matching the stored data. */
const DISALLOWED = /[^A-Za-z\u00C0-\u024F '-]/g;

/** Curly apostrophes, as pasted out of Word. Converted rather than dropped. */
const CURLY_APOSTROPHES = /[\u2018\u2019\u02BC]/g;

/** Typographic dashes, likewise. */
const LONG_DASHES = /[\u2010-\u2015\u2212]/g;

function sanitize(value: string): string {
	return value
		.replace(CURLY_APOSTROPHES, '\'')
		.replace(LONG_DASHES, '-')
		.replace(DISALLOWED, '');
}

@Directive({
	selector: '[appNameInput]'
})
export class NameInputDirective {
	constructor(private el: ElementRef<HTMLInputElement>) {}

	@HostListener('keypress', ['$event'])
	onKeyPress(event: KeyboardEvent): void {
		// Enter, Tab and the like arrive as multi-character key names and must
		// not be swallowed along with the symbols.
		if (event.key && event.key.length === 1 && sanitize(event.key) === '') {
			event.preventDefault();
		}
	}

	@HostListener('input')
	onInput(): void {
		const input = this.el.nativeElement;
		const cleaned = sanitize(input.value);

		if (cleaned === input.value) {
			return;
		}

		// Characters removed ahead of the caret would otherwise drop it to the
		// end of the field mid-edit.
		const caret = sanitize(input.value.substr(0, input.selectionStart)).length;

		input.value = cleaned;
		input.setSelectionRange(caret, caret);

		// The value accessor behind formControlName listens for this same
		// event and may have already read the raw value. A second event hands
		// it the cleaned one whichever order the two listeners run in; it
		// re-enters this handler once and returns at the check above.
		input.dispatchEvent(new Event('input', { bubbles: true }));
	}
}
